import { View, StyleSheet, Animated, Easing, Platform } from 'react-native';
import { useEffect, useRef } from 'react';
import { useTheme } from '@/theme';

type SkeletonLoaderProps = {
  width?: number | string;
  height?: number;
  borderRadius?: number;
  style?: any;
};

export function SkeletonLoader({
  width = '100%',
  height = 20,
  borderRadius = 8,
  style,
}: SkeletonLoaderProps) {
  const theme = useTheme();
  const animatedValue = useRef(new Animated.Value(0)).current;
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const useNativeDriver = Platform.OS !== 'web';
  const baseColor = Platform.OS === 'web' ? '#e2e8f0' : theme.colors.borderMuted;
  const shimmerColor = Platform.OS === 'web' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.3)';

  useEffect(() => {
    // Pulse animation
    const pulseAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver,
        }),
        Animated.timing(animatedValue, {
          toValue: 0,
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver,
        }),
      ])
    );
    pulseAnimation.start();

    // Shimmer animation
    const shimmerAnimation = Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1500,
        easing: Easing.linear,
        useNativeDriver,
      })
    );
    shimmerAnimation.start();

    return () => {
      pulseAnimation.stop();
      shimmerAnimation.stop();
    };
  }, [animatedValue, shimmerAnim, useNativeDriver]);

  const opacity = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0.4, 0.8],
  });

  const shimmerTranslateX = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-200, 200],
  });

  return (
    <View
      style={[
        {
          width,
          height,
          borderRadius,
          backgroundColor: baseColor,
          overflow: 'hidden',
        },
        style,
      ]}
    >
      <Animated.View
        style={{
          flex: 1,
          opacity,
        }}
      />
      <Animated.View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          transform: [{ translateX: shimmerTranslateX }],
          backgroundColor: shimmerColor,
          width: '50%',
        }}
      />
    </View>
  );
}

export function TripCardSkeleton() {
  const theme = useTheme();
  const isWeb = Platform.OS === 'web';
  
  return (
    <View
      style={{
        flexDirection: 'row',
        gap: theme.spacing(3),
        paddingVertical: theme.spacing(2.5),
        paddingHorizontal: theme.spacing(2.5),
        backgroundColor: theme.colors.surface,
        borderRadius: theme.radii.xl,
        marginHorizontal: theme.spacing(2),
        marginVertical: theme.spacing(1),
        borderWidth: isWeb ? 1 : 0,
        borderColor: isWeb ? '#e2e8f0' : 'transparent',
        ...theme.shadows.level0,
      }}
    >
      <SkeletonLoader width={120} height={120} borderRadius={theme.radii.xl} />
      <View style={{ flex: 1, gap: theme.spacing(1.5), justifyContent: 'center' }}>
        <SkeletonLoader width="75%" height={24} borderRadius={theme.radii.sm} />
        <SkeletonLoader width="60%" height={18} borderRadius={theme.radii.sm} />
        <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(0.5) }}>
          <SkeletonLoader width={60} height={20} borderRadius={theme.radii.pill} />
          <SkeletonLoader width={80} height={20} borderRadius={theme.radii.sm} />
        </View>
        <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), marginTop: theme.spacing(0.75), flexWrap: 'wrap' }}>
          <SkeletonLoader width={90} height={24} borderRadius={theme.radii.sm} />
          <SkeletonLoader width={100} height={24} borderRadius={theme.radii.sm} />
        </View>
      </View>
    </View>
  );
}

export function DocumentCardSkeleton() {
  const theme = useTheme();
  
  return (
    <View
      style={{
        flexDirection: 'row',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.colors.surface,
        borderRadius: theme.radii.lg,
        borderWidth: 1,
        borderColor: theme.colors.borderMuted,
      }}
    >
      <SkeletonLoader width={60} height={60} borderRadius={theme.radii.md} />
      <View style={{ flex: 1, gap: theme.spacing(0.75), justifyContent: 'center' }}>
        <SkeletonLoader width="70%" height={18} borderRadius={theme.radii.sm} />
        <SkeletonLoader width="50%" height={14} borderRadius={theme.radii.sm} />
        <SkeletonLoader width="40%" height={14} borderRadius={theme.radii.sm} />
      </View>
    </View>
  );
}

export function ListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonLoader key={index} width="100%" height={60} borderRadius={8} style={{ marginBottom: 12 }} />
      ))}
    </>
  );
}

